import { createMuxClient } from "@mux/sdk/v2/client"
import type { ServerConnection } from "@/context/server"
import { browserActorID } from "@/storage/client-actor"
import { invalidateHealthyRoute } from "@/transport/health-probe-cache"
import { connectionError } from "@/transport/connection-error"
import { sealedFetch } from "@/transport/e2e"
import { isRouteGatewayFailure } from "@/transport/route-errors"
import { abortable } from "@/utils/abortable"

const clientActorHeader = "X-Mux-Client-Actor"
const unavailableSDKBaseURL = "http://mux.invalid"
type ResponseObserver = (response: Response, server: ServerConnection.HttpBase, signal?: AbortSignal | null) => void

export class ServerUnavailableError extends Error {
  constructor() {
    super("Server not available")
    this.name = "ServerUnavailableError"
  }
}

export function serverAuthHeaders(
  server: ServerConnection.HttpBase,
  pageOrigin = typeof location === "undefined" ? undefined : location.origin,
) {
  const url = (() => {
    try {
      return new URL(server.url, pageOrigin)
    } catch {
      return undefined
    }
  })()
  const relayPattern = /\/api\/machines\/[^/]+\/relay(?:\/|$)/
  const relay = relayPattern.test(url?.pathname ?? "") || relayPattern.test(server.url)
  if (server.accessToken) {
    if (relay) return { "X-Mux-Machine-Authorization": `Bearer ${server.accessToken}` }
    if (pageOrigin && url?.origin === pageOrigin) return {}
    return { Authorization: `Bearer ${server.accessToken}` }
  }
  if (!server.password) return {}
  return { Authorization: `Basic ${btoa(`${server.username ?? "mux"}:${server.password}`)}` }
}

/**
 * A tunnel can stall without failing: a request reaches neither the machine nor
 * an error. A read still waiting after this long asks the route owner to check
 * every route, this one included, so a slow handler on a healthy route keeps
 * its route. When a different route wins, the read is sent again there.
 */
export const requestStallMs = 2_500

export async function serverFetch(
  server: ServerConnection.HttpBase,
  input: string,
  init?: RequestInit,
  options?: { resolveServer?: () => ServerConnection.HttpBase; fetch?: typeof globalThis.fetch; onResponse?: ResponseObserver },
) {
  const execute = (current: ServerConnection.HttpBase, signal = init?.signal) => {
    const base = current.url.replace(/\/+$/, "")
    const path = input.startsWith("/") ? input : `/${input}`
    const headers = new Headers(init?.headers)
    headers.set(clientActorHeader, browserActorID())
    const target = `${base}${path}`
    for (const [key, value] of Object.entries(serverAuthHeaders({ ...current, url: target }))) headers.set(key, value)
    const credentials = init?.credentials === "omit" ? "same-origin" : init?.credentials ?? "same-origin"
    const fetchImpl = options?.fetch ?? globalThis.fetch
    const sent = current.e2eKey
      ? sealedFetch(fetchImpl, current.e2eKey, base, path, { ...init, headers, signal })
      : fetchImpl(target, { ...init, headers, credentials, signal })
    const request = sent.catch((error: unknown) => {
      if (error instanceof TypeError && !signal?.aborted) invalidateHealthyRoute(current)
      throw connectionError(error, init?.method, signal)
    })
    if (!options?.onResponse) return request
    return request.then(response => {
      options.onResponse!(response, current, init?.signal)
      return response
    })
  }

  init?.signal?.throwIfAborted()
  const resolved = options?.resolveServer?.() ?? server
  // A read resent after a stall is answered by, and recovers through, its new route.
  let current = resolved.prepare ? await abortable(resolved.prepare(), init?.signal) : resolved
  init?.signal?.throwIfAborted()
  const safe = ["GET", "HEAD"].includes((init?.method ?? "GET").toUpperCase())
  const executeWatched = async () => {
    const recover = current.recover
    // Writes are never sent twice, so only reads are watched.
    if (!safe || !recover) return execute(current)
    const stalledRoute = current
    const attempt = new AbortController()
    const first = execute(stalledRoute, init?.signal ? AbortSignal.any([init.signal, attempt.signal]) : attempt.signal)
    let settled = false
    const done = first.then(() => { settled = true }, () => { settled = true })
    let timer: ReturnType<typeof setTimeout> | undefined
    const stalled = await Promise.race([
      done.then(() => false),
      new Promise<boolean>(resolve => { timer = setTimeout(() => resolve(true), requestStallMs) }),
    ])
    clearTimeout(timer)
    if (!stalled || init?.signal?.aborted) return first
    const recovery = recover("request-stall", init?.signal ?? undefined).catch(() => undefined)
    const recovered = await Promise.race([done.then(() => undefined), recovery])
    if (settled || !recovered || recovered.url === stalledRoute.url || init?.signal?.aborted) return first
    attempt.abort()
    current = recovered
    return execute(recovered)
  }
  let response: Response
  try {
    response = await executeWatched()
  } catch (error) {
    if (!(error instanceof TypeError) || init?.signal?.aborted) throw error
    if (!current.recover) throw error
    const recovered = await abortable(current.recover("network-error", init?.signal ?? undefined).catch(() => undefined), init?.signal)
    if (!recovered || !safe || init?.signal?.aborted) throw error
    return execute(recovered)
  }
  if (response.status === 401 && current.prepare && current.accessToken && !init?.signal?.aborted) {
    const headers = serverAuthHeaders(current)
    if (headers.Authorization?.startsWith("Bearer ") || headers["X-Mux-Machine-Authorization"]) {
      if (!safe) {
        // Remember rejection and renew for future calls, never replay this write.
        void current.prepare(current.accessToken).catch(() => {})
        return response
      }
      const refreshed = await abortable(current.prepare(current.accessToken), init?.signal).catch((error) => {
        void response.body?.cancel().catch(() => {})
        throw error
      })
      init?.signal?.throwIfAborted()
      if (refreshed && refreshed.accessToken !== current.accessToken) {
        void response.body?.cancel().catch(() => {})
        return execute(refreshed)
      }
    }
  }
  if (!isRouteGatewayFailure(response.status) || init?.signal?.aborted) return response
  invalidateHealthyRoute(current)
  if (!current.recover) return response
  const recovered = await abortable(current.recover("gateway-error", init?.signal ?? undefined).catch(() => undefined), init?.signal).catch(error => {
    void response.body?.cancel().catch(() => {})
    throw error
  })
  if (!recovered || !safe || init?.signal?.aborted) return response
  void response.body?.cancel().catch(() => {})
  return execute(recovered)
}

export function createSdkForServer({
  server,
  resolveServer,
  onResponse,
  ...config
}: Omit<NonNullable<Parameters<typeof createMuxClient>[0]>, "baseUrl"> & {
  server?: ServerConnection.HttpBase
  resolveServer?: () => ServerConnection.HttpBase | undefined
  onResponse?: ResponseObserver
}) {
  const sdkBase = server?.url ?? unavailableSDKBaseURL
  const sdkBaseUrl = new URL(sdkBase.replace(/\/+$/, ""))
  const fetchWithSiteAuth: typeof globalThis.fetch = async (input, init) => {
    const current = resolveServer?.() ?? server
    if (!current) throw new ServerUnavailableError()
    const request = new Request(input, init)
    const rawUrl = request.url
    const url = new URL(rawUrl, current.url)
    const path = url.origin === sdkBaseUrl.origin && url.pathname.startsWith(sdkBaseUrl.pathname)
      ? `${url.pathname.slice(sdkBaseUrl.pathname.replace(/\/$/, "").length)}${url.search}`
      : rawUrl
    const headers = new Headers(request.headers)
    const body = request.method === "GET" || request.method === "HEAD" ? undefined : await request.arrayBuffer()
    const requestInit: RequestInit = {
      method: request.method,
      headers,
      body,
      signal: request.signal,
      credentials: request.credentials,
      cache: request.cache,
      integrity: request.integrity,
      keepalive: request.keepalive,
      mode: request.mode,
      redirect: request.redirect,
      referrer: request.referrer,
      referrerPolicy: request.referrerPolicy,
    }
    return serverFetch(current, path, requestInit, { fetch: config.fetch ?? globalThis.fetch, onResponse })
  }

  return createMuxClient({
    ...config,
    fetch: fetchWithSiteAuth,
    headers: config.headers,
    baseUrl: sdkBase,
  })
}
